import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit, inject, signal, WritableSignal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DonationService } from '../../donation/donation.service';
import { NotificationService } from '../../notification/notification.service';
import { ResolutionService } from '../../resolution.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-voluntary-donation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormField,
    MatLabel,
    CurrencyPipe
  ],
  templateUrl: './voluntary-donation.component.html',
  styleUrl: './voluntary-donation.component.scss'
})
export class VoluntaryDonationComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private donationService = inject(DonationService);
  private notificationService = inject(NotificationService);
  private resolutionService = inject(ResolutionService);

  donationAmount: number | null = null;
  displayAmount: string = '';
  selectedOption: WritableSignal<string> = signal('CUSTOM');
  isLoading: WritableSignal<boolean> = signal(false);
  isMobile = computed(() => this.resolutionService.isMobile());

  predefinedAmounts = [5, 10, 25, 50, 100];

  ngOnInit() {}

  selectPredefinedAmount(amount: number) {
    this.selectedOption.set('CUSTOM');
    this.donationAmount = amount;
    this.displayAmount = '$' + amount.toFixed(2);
  }

  onAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Remove all non-numeric except decimal point
    value = value.replace(/[^\d.]/g, '');
    
    // Limit to one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    // Update the numeric amount
    this.donationAmount = value ? parseFloat(value) : null;
    
    // Update display with $ prefix
    this.displayAmount = value ? '$' + value : '';
    
    // Set cursor position after $
    setTimeout(() => {
      const cursorPos = input.selectionStart || 0;
      input.value = this.displayAmount;
      if (cursorPos === 0 && this.displayAmount) {
        input.setSelectionRange(1, 1);
      } else {
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
  }

  donate() {
    const amount = this.donationAmount;

    if (!amount || amount <= 0) {
      this.notificationService.show({
        text: 'Please enter a valid donation amount',
        type: 'error'
      });
      return;
    }

    this.isLoading.set(true);

    this.donationService.contribute(amount)
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          this.notificationService.show({
            text: 'Thank you for your generous donation!',
            type: 'success'
          });
          this.activeModal.close({ success: true, amount });
        },
        error: (error) => {
          this.notificationService.show({
            text: 'There was an error processing your donation. Please try again.',
            type: 'error'
          });
        }
      });
  }

  closeModal() {
    this.activeModal.close();
  }
}

